import React from 'react';
import { useForm } from 'react-hook-form';
import { Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface SupportForm {
  subject: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

const Support: React.FC = () => {
  const { user } = useAuth();
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<SupportForm>();

  const onSubmit = async (data: SupportForm) => {
    try {
      await api.post('/api/support/tickets', data);
      alert('Your support ticket has been submitted. We will get back to you soon.');
      reset();
    } catch (error) {
      console.error('Error sending support request:', error);
      alert('There was an error sending your message. Please try again.');
    }
  };

  return (
    <div className="py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Support</h1>
      
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-md p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Subject</label>
              <input
                type="text"
                {...register('subject', { required: 'Subject is required' })}
                className="mt-1 block w-full rounded-md"
                placeholder="Brief description of your issue"
              />
              {errors.subject && (
                <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Priority</label>
              <select
                {...register('priority')}
                className="mt-1 block w-full rounded-md"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Message</label>
              <textarea
                {...register('message', { required: 'Message is required' })}
                rows={6}
                className="mt-1 block w-full rounded-md"
                placeholder="Describe your issue in detail"
              />
              {errors.message && (
                <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
              )}
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center space-x-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
            >
              <Send className="h-4 w-4" />
              <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Support;